import { LEGO_COLORS, normalizeColorIndex } from "../constants/legoColors";

// Renders the same per-cell style as PixelBrickCanvas (the on-screen
// preview): baseplate fill, cell rectangle, 1-px shadow/light edges, and a
// circular stud when the brick is at least 10 px. Kept in sync manually
// with PixelBrickCanvas — the on-screen canvas uses a ResizeObserver-driven
// brick size, while this renderer uses a fixed pixel-per-cell for high-res
// sharing/printing.
export const MOSAIC_BASEPLATE_COLOR = "#e0d9cd";
export const DEFAULT_BRICK_PX = 32;

export interface MosaicPngOptions {
  /** Pixels per brick cell. Defaults to 32 (high-res for sharing). */
  brickSizePx?: number;
  /** Background color drawn behind the bricks. Defaults to the baseplate. */
  background?: string;
}

export function drawMosaic(
  ctx: CanvasRenderingContext2D,
  pixels: number[][],
  width: number,
  height: number,
  options: MosaicPngOptions = {},
): void {
  const {
    brickSizePx = DEFAULT_BRICK_PX,
    background = MOSAIC_BASEPLATE_COLOR,
  } = options;
  const bs = brickSizePx;
  const showStuds = bs >= 10;

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width * bs, height * bs);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const ci = pixels[y]?.[x] ?? 0;
      const color = LEGO_COLORS[normalizeColorIndex(ci)];
      const px = x * bs;
      const py = y * bs;

      ctx.fillStyle = `rgb(${color.r},${color.g},${color.b})`;
      ctx.fillRect(px + 0.5, py + 0.5, bs - 1, bs - 1);

      ctx.fillStyle = "rgba(0,0,0,0.08)";
      ctx.fillRect(px + bs - 1.5, py + 0.5, 1, bs - 1);
      ctx.fillRect(px + 0.5, py + bs - 1.5, bs - 1, 1);

      ctx.fillStyle = "rgba(255,255,255,0.12)";
      ctx.fillRect(px + 0.5, py + 0.5, bs - 2, 1);
      ctx.fillRect(px + 0.5, py + 0.5, 1, bs - 2);

      if (showStuds) {
        const cx = px + bs / 2;
        const cy = py + bs / 2;
        const sr = bs * 0.2;

        ctx.beginPath();
        ctx.arc(cx, cy, sr, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.18)";
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.12)";
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }
  }
}

export function mosaicToPngDataUrl(
  pixels: number[][],
  width: number,
  height: number,
  options: MosaicPngOptions = {},
): string {
  const { brickSizePx = DEFAULT_BRICK_PX } = options;
  const canvas = document.createElement("canvas");
  canvas.width = width * brickSizePx;
  canvas.height = height * brickSizePx;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("mosaicToPngDataUrl: 2D canvas context unavailable");
  }
  drawMosaic(ctx, pixels, width, height, { ...options, brickSizePx });
  return canvas.toDataURL("image/png");
}

export function exportMosaicPng(
  pixels: number[][],
  width: number,
  height: number,
  filename = "bricksup-mosaic.png",
  options: MosaicPngOptions = {},
): void {
  const dataUri = mosaicToPngDataUrl(pixels, width, height, options);

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", filename);
  linkElement.click();
}